"use client";

import { useEffect, useRef } from "react";

import type { Ad } from "../../../../types/ads/adResource";
import type { Article } from "../../../../types/content";
import type { PlaceholderConfigs } from "../../../../types/injectables";
import type { Theme } from "../../../../types/theme";

import { getAdId, getApnTag, getPPIDWithTagValues } from ".";
import { isConsentToAll, useTcfPermission } from "../../../../tcf/helpers";
import { getIdentitySession } from "../../../../utils/auth/getSessionFromIdentity";
import {
  resetInitializationGate,
  signalInitializationComplete,
} from "./adInitializationGate";
import { getGeoTrackingPermission } from "./getGeoTrackingPermission";
import { getKeywords } from "./getKeywords";
import { tagAndBatch } from "./tagAndBatch";

const isDebugActivated = !!process.env.NEXT_PUBLIC_AST_DEBUG_ENABLED || false;
const brandName: Theme =
  process.env.NEXT_PUBLIC_UI_THEME === "ekonomi" ? "ekonomi" : "core";

const SetPageOpts = ({
  userId,
  article,
  topicName,
  isLoggedIn,
  isPremium,
  batchToLoad,
}: {
  userId?: string;
  article?: Article;
  topicName?: string;
  isLoggedIn: boolean;
  isPremium: boolean;
  batchToLoad?: (PlaceholderConfigs | Ad)[];
}) => {
  const marketingConsent = useTcfPermission("CMP:marketing");

  const pathname =
    typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "";
  const lastPathname = useRef<string | null>(null);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    async function setPage(isInitial: boolean) {
      const [consentToAll, { sig, userId: sessionUserId }, apntag, adId] =
        await Promise.all([
          isConsentToAll(),
          getIdentitySession(isLoggedIn),
          getApnTag(),
          getAdId(),
        ]);

      if (!isInitial && apntag.clearRequest && !article) {
        apntag.clearRequest();
      }

      const [geoTracking, [ppIds, ppIdsTags]] = await Promise.all([
        getGeoTrackingPermission(isLoggedIn, sessionUserId, sig),
        getPPIDWithTagValues(userId || sessionUserId, brandName === "ekonomi"),
      ]);

      const keywords = await getKeywords({
        geoLocationTracking: geoTracking,
        user: { isLoggedIn, isPremium },
        topicName,
        article,
        consentToAll,
      });

      if (isDebugActivated && apntag?.loaded) {
        apntag.enableDebug();
      }

      apntag.anq.push(function () {
        apntag.setPageOpts({
          member: 9943,
          keywords: {
            ...keywords,
            ...ppIdsTags,
          },
          trackingManagement: {
            native: {
              loadViewabilityScriptAt: "impression",
            },
          },
          user: {
            externalUid: marketingConsent || consentToAll ? undefined : adId,
            userIds: [{ type: "extendedIDs", eids: ppIds }],
          },
        });
      });

      await tagAndBatch(batchToLoad, apntag);

      // Signal that initialization is complete - ads can now start loading
      signalInitializationComplete();

      return;
    }

    if (isInitialLoad.current) {
      setPage(true);
      isInitialLoad.current = false;
      lastPathname.current = pathname;
    } else if (lastPathname.current !== pathname) {
      // Reset the gate before starting new page initialization
      resetInitializationGate();
      setPage(false);
      lastPathname.current = pathname;
    }
  }, [
    userId,
    article,
    isLoggedIn,
    isPremium,
    marketingConsent,
    pathname,
    batchToLoad,
    topicName,
  ]);
  return null;
};

export { SetPageOpts };
